/*!
 * Reflet Design — configurateur photo
 * Module autonome : vanilla JS, aucune librairie, aucune dépendance Shopify.
 * Version 0.2.0 — 2026-10-20 (V3 du site : options « moteur seul », sans recadrage ni alerte)
 *
 * NOUVEAU EN 0.2.0 (toutes les options sont facultatives ; sans elles, rien ne change) :
 *   interface: false      -> le module ne dessine aucune commande : seul le champ fichier
 *                            (caché) est posé dans rootEl. Le site dessine ses propres boutons
 *                            et pilote le module par l'API (setFormat, ouvrirFichier…).
 *   recadrage: false      -> pas de zone de recadrage ni de zoom : la photo est centrée et
 *                            remplit le format (recadrage centré automatique).
 *   alerteNettete: false  -> aucune jauge, aucun « trop petit », aucun blocage. La netteté
 *                            (dpi) reste calculée en silence et part dans _Netteté.
 *   droitsRequis: false   -> pas de case à cocher : le site affiche sa propre phrase de
 *                            consentement ; _Droits photo = « Confirmés à l'ajout au panier ».
 *   Événement 'reflet:erreur' (detail = { code, message }) quand une photo est refusée.
 *   cfg.ouvrirFichier() : ouvre le sélecteur de photo.
 *
 * CONTRAT (avec les autres modules du site) :
 *
 *   var cfg = window.RefletConfigurateur.mount(rootEl, options);
 *
 *   rootEl reçoit :
 *     - 'reflet:change' à chaque changement (photo, format, orientation, recadrage, cases).
 *       detail = { imageUrl, imageNaturalW, imageNaturalH, crop:{x,y,w,h}, format,
 *                  orientation, dpi, niveau:'bon'|'correct'|'faible', droitsOK, ... }
 *     - 'reflet:submit' quand le bouton « Ajouter au panier » du module est cliqué
 *       et que tout est valide. detail = { formData, etat }.
 *
 *   cfg.getFormData()  -> FormData (fichier original + aperçu recadré en Blob + paramètres)
 *   cfg.getState()     -> le même objet que detail de 'reflet:change'
 *   cfg.validate()     -> { ok:Boolean, erreurs:[{code, message}] }
 *   cfg.setFormat(id), cfg.setOrientation('portrait'|'paysage'), cfg.loadFile(file),
 *   cfg.reset(), cfg.destroy()
 *
 * Le module ne dessine NI le décor NI la 3D. Il fournit la zone de recadrage et les
 * contrôles, marqués par des classes .reflet-cfg-* que le site stylise.
 *
 * Coordonnées de recadrage : en pixels de la photo ORIGINALE telle qu'affichée
 * (orientation EXIF appliquée par le navigateur). imageUrl pointe vers une copie de
 * travail allégée (côté long ≤ 2048 px par défaut) : pour texturer, utiliser cropNorm
 * (fractions de 0 à 1), valable quelle que soit la résolution.
 *
 * Étiquettes (standard de preuve de l'agence) :
 *   - seuils 150 / 100 dpi : RECOMMENDATION (rapport G §6.3), à valider sur la machine ;
 *   - délais (contrôle 1, fabrication 2, séchage 1) : réglages de l'atelier, ASSUMPTION ;
 *   - délai postal Suisse → France 3 jours ouvrés : ASSUMPTION ;
 *   - date limite de Noël France 2026-11-26 : à confirmer selon le mode d'envoi
 *     (dernier dépôt La Poste vers l'Europe le 01/12/2026 : FACT, rapport D) ;
 *   - limite de 20 Mo par photo : ASSUMPTION (limite Shopify rapportée par des sources
 *     tierces, non trouvée dans la doc officielle) ;
 *   - conservation « 90 jours après la livraison » : À VALIDER (juriste).
 */
(function (racine, fabrique) {
  'use strict';
  var api = fabrique(racine);
  if (typeof module === 'object' && module && module.exports) { module.exports = api; }
  if (racine && racine.document) { racine.RefletConfigurateur = api; }
})(typeof window !== 'undefined' ? window : this, function (win) {
  'use strict';

  var VERSION = '0.2.0';
  var doc = win && win.document;

  /* ------------------------------------------------------------------ */
  /* Réglages par défaut                                                 */
  /* ------------------------------------------------------------------ */

  var DEFAUTS = {
    formats: [
      { id: '15x20', w: 15, h: 20, prix: null, devise: 'EUR' },
      { id: '20x30', w: 20, h: 30, prix: null, devise: 'EUR' },
      { id: '30x40', w: 30, h: 40, prix: null, devise: 'EUR' },
      { id: '40x60', w: 40, h: 60, prix: null, devise: 'EUR' }
    ],
    formatInitial: null,              // id du format coché au départ (défaut : le premier disponible)
    orientationInitiale: 'portrait',  // 'portrait' | 'paysage'
    orientationAuto: true,            // à l'envoi, l'orientation suit celle de la photo
    seuils: { bon: 150, min: 100 },   // dpi — RECOMMENDATION
    delais: { controle: 1, fabrication: 2, sechage: 1, poste: 3 }, // jours ouvrés — ASSUMPTION
    fermetures: ['2026-12-25', '2027-01-01'], // jours sans travail ni poste — ASSUMPTION
    fuseau: 'Europe/Zurich',
    aujourdhui: null,                 // 'AAAA-MM-JJ' pour forcer la date (tests, démo)
    noel: {
      date: '2026-11-26',             // à confirmer selon le mode d'envoi
      texte: 'Pour Noël, commandez avant le [date].',
      dernierDepot: '2026-12-01'      // FACT (rapport D) : dernier dépôt La Poste vers l'Europe
    },
    conservation: '90 jours après la livraison', // À VALIDER
    liens: { confidentialite: 'confidentialite.html' },
    tailleMaxMo: 20,                  // ASSUMPTION
    margeSecuriteMm: 3,               // zone de sécurité pointillée — ASSUMPTION
    zoomMax: 4,
    travail: { cote: 2048 },          // copie de travail affichée
    apercu: { cote: 1200, qualite: 0.85 }, // aperçu recadré joint au FormData
    bouton: true,                     // affiche le bouton « Ajouter au panier » du module
    interface: true,                  // false : moteur seul, le site dessine les commandes (0.2.0)
    recadrage: true,                  // false : recadrage centré automatique, sans zone ni zoom (0.2.0)
    alerteNettete: true,              // false : netteté calculée en silence, aucune alerte (0.2.0)
    droitsRequis: true,               // false : pas de case, consentement porté par le site (0.2.0)
    champs: {                         // noms des champs du FormData (null = champ omis)
      variante: 'id',
      quantite: 'quantity',
      format: 'properties[Format]',
      orientation: 'properties[Orientation]',
      photo: 'properties[Photo originale]',
      apercu: 'properties[Aperçu recadré]',
      recadrage: 'properties[_Recadrage]',
      nettete: 'properties[_Netteté]',
      alerte: 'properties[_Alerte netteté acceptée]',
      droits: 'properties[_Droits photo]',
      livraison: 'properties[_Livraison estimée]'
    },
    textes: {
      titreFormat: 'Choisissez le format',
      titrePhoto: 'Ajoutez votre photo',
      titreCadrage: 'Cadrez votre photo',
      notePrix: 'livraison comprise',
      conseille: 'Conseillé',
      boutonPhoto: 'Choisir ma photo',
      boutonPhotoChanger: 'Changer de photo',
      aidePhoto: 'Une photo de téléphone suffit le plus souvent. JPEG ou PNG.',
      chargement: 'Ouverture de la photo…',
      portrait: 'Portrait',
      paysage: 'Paysage',
      orientation: 'Orientation',
      zoneVide: 'Ajoutez votre photo pour la cadrer.',
      zoneAria: 'Zone de recadrage. Glissez pour déplacer. Flèches : déplacer. Plus et moins : zoomer.',
      consigne: 'Glissez pour déplacer la photo. Pincez, utilisez la molette ou le curseur pour zoomer.',
      securite: 'Gardez les visages à l’intérieur du pointillé.',
      zoom: 'Zoom',
      zoomMoins: 'Dézoomer',
      zoomPlus: 'Zoomer',
      recentrer: 'Recentrer',
      niveau_bon: 'Parfait',
      niveau_correct: 'Correct',
      niveau_faible: 'Trop petit pour ce format',
      detail_bon: 'Votre photo sera nette en [format].',
      detail_correct: 'Nette à distance normale, un peu moins de très près.',
      detail_faible: 'Votre photo est trop petite pour le [format] : elle risque d’être floue.',
      dpi: '[dpi] points par pouce à la taille du tableau',
      suggestion: 'En [format], elle sera nette.',
      suggestionBouton: 'Passer en [format]',
      dezoomer: 'Vous pouvez aussi dézoomer : la photo gagnera en netteté.',
      forcer: 'J’ai compris, je veux quand même ce format',
      droits: 'Je confirme avoir le droit d’utiliser cette photo.',
      lienConfidentialite: 'Politique de confidentialité',
      conservation: 'Votre photo sert seulement à fabriquer votre tableau. Elle est supprimée [delai].',
      livraison: 'Livraison estimée : [date]',
      livraisonNote: 'estimation en jours ouvrés',
      apresNoel: 'Arrivée prévue après Noël.',
      bouton: 'Ajouter au panier',
      erreurHeic: 'Votre navigateur ne sait pas lire les photos HEIC. Sur iPhone, choisissez la photo depuis Photos : elle sera envoyée en JPEG. Sur ordinateur, enregistrez-la d’abord en JPEG.',
      erreurLecture: 'Ce fichier ne s’ouvre pas comme une photo. Essayez une photo JPEG ou PNG.',
      erreurType: 'Ce fichier n’est pas une photo. Choisissez une image JPEG ou PNG.',
      erreurTaille: 'Cette photo pèse [taille] Mo : la limite est de [max] Mo. Choisissez une version plus légère.',
      manquePhoto: 'Ajoutez votre photo.',
      manqueFormat: 'Ce format n’est pas disponible.',
      manqueNettete: 'Votre photo est trop petite pour ce format. Choisissez un format plus petit, ou cochez « J’ai compris ».',
      manqueDroits: 'Cochez la case pour confirmer vos droits sur la photo.'
    }
  };

  /* ------------------------------------------------------------------ */
  /* Fonctions pures (testables sans navigateur)                         */
  /* ------------------------------------------------------------------ */

  var JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  var MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août',
    'septembre', 'octobre', 'novembre', 'décembre'];

  function borner(v, min, max) {
    if (min > max) { return (min + max) / 2; }
    return v < min ? min : (v > max ? max : v);
  }

  function estObjet(v) { return v !== null && typeof v === 'object' && !Array.isArray(v); }

  /* Fusion profonde simple : les tableaux et valeurs scalaires remplacent. */
  function fusionner(base, ajout) {
    var res = {};
    var k;
    for (k in base) { if (Object.prototype.hasOwnProperty.call(base, k)) { res[k] = estObjet(base[k]) ? fusionner(base[k], {}) : base[k]; } }
    if (!estObjet(ajout)) { return res; }
    for (k in ajout) {
      if (!Object.prototype.hasOwnProperty.call(ajout, k) || ajout[k] === undefined) { continue; }
      res[k] = (estObjet(ajout[k]) && estObjet(res[k])) ? fusionner(res[k], ajout[k]) : ajout[k];
    }
    return res;
  }

  /* Remplace [cle] par valeurs.cle dans un texte. */
  function gabarit(texte, valeurs) {
    return String(texte == null ? '' : texte).replace(/\[([a-z]+)\]/gi, function (m, cle) {
      return Object.prototype.hasOwnProperty.call(valeurs || {}, cle) ? String(valeurs[cle]) : m;
    });
  }

  function normaliserFormat(f, i) {
    var w = Number(f && f.w);
    var h = Number(f && f.h);
    if (!(w > 0) || !(h > 0)) { return null; }
    var prix = (f.prix === null || f.prix === undefined || f.prix === '') ? null : Number(f.prix);
    return {
      id: String(f.id != null ? f.id : (w + 'x' + h)),
      w: w,
      h: h,
      prix: (prix === null || isNaN(prix)) ? null : prix,
      devise: f.devise || 'EUR',
      libelle: f.libelle || (w + '×' + h + ' cm'),
      variantId: f.variantId != null ? f.variantId : null,
      disponible: f.disponible !== false,
      ordre: i
    };
  }

  /* Dimensions d'impression en cm selon l'orientation (portrait = côté court en largeur). */
  function dimensionsCm(format, orientation) {
    var court = Math.min(format.w, format.h);
    var long = Math.max(format.w, format.h);
    return orientation === 'paysage' ? { largeur: long, hauteur: court } : { largeur: court, hauteur: long };
  }

  function ratioFormat(format, orientation) {
    var d = dimensionsCm(format, orientation);
    return d.largeur / d.hauteur;
  }

  /* Plus grand rectangle de ratio r (largeur / hauteur) contenu dans une image iw × ih. */
  function recadrageMax(iw, ih, r) {
    if (iw / ih > r) { return { w: ih * r, h: ih }; }
    return { w: iw, h: iw / r };
  }

  /* Netteté : pixels de la zone recadrée ÷ taille d'impression en pouces (le plus faible des deux côtés). */
  function calculerDpi(cropW, cropH, largeurCm, hauteurCm) {
    if (!(cropW > 0) || !(cropH > 0) || !(largeurCm > 0) || !(hauteurCm > 0)) { return 0; }
    return Math.min(cropW / (largeurCm / 2.54), cropH / (hauteurCm / 2.54));
  }

  function niveauNettete(dpi, seuils) {
    if (dpi >= seuils.bon) { return 'bon'; }
    if (dpi >= seuils.min) { return 'correct'; }
    return 'faible';
  }

  /* Netteté maximale possible pour un format (zoom 1, ou zoom donné). */
  function dpiPourFormat(iw, ih, format, orientation, zoom) {
    var r = ratioFormat(format, orientation);
    var m = recadrageMax(iw, ih, r);
    var z = zoom > 1 ? zoom : 1;
    var d = dimensionsCm(format, orientation);
    return calculerDpi(m.w / z, m.h / z, d.largeur, d.hauteur);
  }

  /* Plus grand format (par surface) qui atteint le niveau demandé, ou null. */
  function formatConseille(formats, iw, ih, orientation, seuils, niveauVise) {
    var meilleur = null;
    for (var i = 0; i < formats.length; i++) {
      var f = formats[i];
      if (!f.disponible) { continue; }
      var dpi = dpiPourFormat(iw, ih, f, orientation, 1);
      var ok = niveauVise === 'bon' ? dpi >= seuils.bon : dpi >= seuils.min;
      if (ok && (!meilleur || f.w * f.h > meilleur.w * meilleur.h)) { meilleur = f; }
    }
    return meilleur;
  }

  /* Format plus petit que « actuel » qui reste au moins « correct » ; sinon le plus petit. */
  function formatPlusPetit(formats, actuel, iw, ih, orientation, seuils) {
    var surface = actuel.w * actuel.h;
    var choix = null;
    var plusPetit = null;
    for (var i = 0; i < formats.length; i++) {
      var f = formats[i];
      if (!f.disponible || f.id === actuel.id) { continue; }
      var s = f.w * f.h;
      if (s >= surface) { continue; }
      if (!plusPetit || s < plusPetit.w * plusPetit.h) { plusPetit = f; }
      if (dpiPourFormat(iw, ih, f, orientation, 1) >= seuils.min && (!choix || s > choix.w * choix.h)) { choix = f; }
    }
    return choix || plusPetit;
  }

  /* Dates : chaînes 'AAAA-MM-JJ', calculées en UTC pour éviter les changements d'heure. */
  function estIso(v) { return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v); }

  function isoVersDate(iso) {
    var p = iso.split('-');
    return new Date(Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2])));
  }

  function dateVersIso(d) { return d.toISOString().slice(0, 10); }

  function estJourOuvre(d, fermetures) {
    var j = d.getUTCDay();
    if (j === 0 || j === 6) { return false; }
    return (fermetures || []).indexOf(dateVersIso(d)) === -1;
  }

  /* Ajoute n jours ouvrés (lundi-vendredi, hors fermetures). Le jour de départ ne compte pas. */
  function ajouterJoursOuvres(iso, n, fermetures) {
    var d = isoVersDate(iso);
    var compte = 0;
    var garde = 0;
    while (compte < n && garde < 1000) {
      d = new Date(d.getTime() + 86400000);
      if (estJourOuvre(d, fermetures)) { compte++; }
      garde++;
    }
    return dateVersIso(d);
  }

  function entierPositif(v) { var n = Math.round(Number(v)); return n > 0 ? n : 0; }

  /* Estimation : dépôt à La Poste après contrôle + fabrication + séchage, puis livraison après le délai postal. */
  function estimationLivraison(aujourdhui, delais, fermetures) {
    var atelier = entierPositif(delais.controle) + entierPositif(delais.fabrication) + entierPositif(delais.sechage);
    var depot = ajouterJoursOuvres(aujourdhui, atelier, fermetures);
    var livraison = ajouterJoursOuvres(depot, entierPositif(delais.poste), fermetures);
    return { depot: depot, livraison: livraison };
  }

  /* La date limite de Noël est-elle tenable avec ces délais ? (dépôt calculé ≤ dernier dépôt) */
  function coherenceNoel(dateLimite, delais, fermetures, dernierDepot) {
    if (!estIso(dateLimite)) { return { depot: null, coherent: null }; }
    var e = estimationLivraison(dateLimite, delais, fermetures);
    return { depot: e.depot, livraison: e.livraison, coherent: estIso(dernierDepot) ? e.depot <= dernierDepot : null };
  }

  function noelVisible(aujourdhui, dateLimite) {
    return estIso(dateLimite) && estIso(aujourdhui) && aujourdhui <= dateLimite;
  }

  function formaterDateFr(iso) {
    if (!estIso(iso)) { return ''; }
    var d = isoVersDate(iso);
    var jour = d.getUTCDate();
    return JOURS[d.getUTCDay()] + ' ' + (jour === 1 ? '1er' : String(jour)) + ' ' + MOIS[d.getUTCMonth()];
  }

  function aujourdhuiIso(fuseau) {
    function deux(n) { return (n < 10 ? '0' : '') + n; }
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: fuseau || 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date());
      var o = {};
      for (var i = 0; i < parts.length; i++) { o[parts[i].type] = parts[i].value; }
      if (o.year && o.month && o.day) { return o.year + '-' + o.month + '-' + o.day; }
    } catch (e) { /* repli ci-dessous */ }
    var d = new Date();
    return d.getFullYear() + '-' + deux(d.getMonth() + 1) + '-' + deux(d.getDate());
  }

  function formaterPrix(prix, devise) {
    if (prix === null || prix === undefined || isNaN(prix)) { return ''; }
    var entier = Math.round(prix * 100) % 100 === 0;
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: devise || 'EUR',
        minimumFractionDigits: entier ? 0 : 2, maximumFractionDigits: 2
      }).format(prix);
    } catch (e) {
      return (entier ? String(Math.round(prix)) : prix.toFixed(2).replace('.', ',')) + ' ' + (devise || '');
    }
  }

  function estHeic(fichier) {
    return /\.(heic|heif)$/i.test(fichier.name || '') || /hei[cf]/i.test(fichier.type || '');
  }

  function texteRecadrage(crop, iw, ih) {
    return 'x=' + crop.x + '; y=' + crop.y + '; largeur=' + crop.w + '; hauteur=' + crop.h +
      '; original=' + iw + 'x' + ih;
  }

  function dataUrlVersBlob(dataUrl) {
    var virgule = dataUrl.indexOf(',');
    var entete = dataUrl.slice(0, virgule);
    var type = (entete.match(/data:([^;]+)/) || [])[1] || 'application/octet-stream';
    var binaire = atob(dataUrl.slice(virgule + 1));
    var octets = new Uint8Array(binaire.length);
    for (var i = 0; i < binaire.length; i++) { octets[i] = binaire.charCodeAt(i); }
    return new Blob([octets], { type: type });
  }

  /* ------------------------------------------------------------------ */
  /* Style fonctionnel minimal (spécificité nulle : le site le remplace) */
  /* ------------------------------------------------------------------ */

  var CSS_BASE = [
    ':where(.reflet-cfg) :where([hidden]){display:none !important}',
    ':where(.reflet-cfg){--reflet-cfg-bon:#2E7D4F;--reflet-cfg-correct:#B26A00;--reflet-cfg-faible:#B3261E}',
    ':where(.reflet-cfg-formats),:where(.reflet-cfg-orientation){display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}',
    ':where(.reflet-cfg-format),:where(.reflet-cfg-orientation-choix){position:relative;display:flex;flex-direction:column;min-width:0;padding:10px 12px;border:1px solid #D9D3CA;border-radius:6px;cursor:pointer}',
    ':where(.reflet-cfg-format.is-actif),:where(.reflet-cfg-orientation-choix.is-actif){border-color:currentColor}',
    ':where(.reflet-cfg-format.is-indisponible){opacity:.45;cursor:not-allowed}',
    ':where(.reflet-cfg-format:focus-within),:where(.reflet-cfg-orientation-choix:focus-within),:where(.reflet-cfg-bouton--photo:focus-within){outline:2px solid currentColor;outline-offset:2px}',
    ':where(.reflet-cfg-format-radio),:where(.reflet-cfg-orientation-radio){position:absolute;opacity:0;width:1px;height:1px;margin:0;pointer-events:none}',
    ':where(.reflet-cfg-format-nettete.reflet-cfg-niveau--bon),:where(.reflet-cfg-jauge.reflet-cfg-niveau--bon) :where(.reflet-cfg-jauge-statut){color:var(--reflet-cfg-bon)}',
    ':where(.reflet-cfg-format-nettete.reflet-cfg-niveau--correct),:where(.reflet-cfg-jauge.reflet-cfg-niveau--correct) :where(.reflet-cfg-jauge-statut){color:var(--reflet-cfg-correct)}',
    ':where(.reflet-cfg-format-nettete.reflet-cfg-niveau--faible),:where(.reflet-cfg-jauge.reflet-cfg-niveau--faible) :where(.reflet-cfg-jauge-statut){color:var(--reflet-cfg-faible)}',
    ':where(.reflet-cfg-fichier){position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;opacity:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}',
    ':where(.reflet-cfg-bouton){display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:10px 18px;border:1px solid currentColor;border-radius:6px;background:none;color:inherit;font:inherit;cursor:pointer}',
    ':where(.reflet-cfg-bouton--photo){position:relative}',
    ':where(.reflet-cfg-bouton.is-bloque){opacity:.6}',
    ':where(.reflet-cfg-zone){position:relative;overflow:hidden;touch-action:none;-webkit-user-select:none;user-select:none;display:flex;align-items:center;justify-content:center;padding:7%;background:#F5F2EC;cursor:grab}',
    ':where(.reflet-cfg-zone.is-vide){cursor:default}',
    ':where(.reflet-cfg-zone.is-glisse){cursor:grabbing}',
    ':where(.reflet-cfg-zone.is-survol){outline:2px dashed currentColor;outline-offset:-6px}',
    ':where(.reflet-cfg-cadre){position:relative;width:100%;max-width:max(var(--reflet-cfg-lmin,220px),calc(var(--reflet-cfg-hmax,60vh) * var(--reflet-cfg-ratio,0.75)));aspect-ratio:var(--reflet-cfg-ratio,0.75)}',
    ':where(.reflet-cfg-image){position:absolute;left:0;top:0;z-index:1;display:block;max-width:none !important;max-height:none !important;min-width:0;transform-origin:0 0;pointer-events:none;-webkit-user-drag:none;user-select:none;will-change:transform}',
    ':where(.reflet-cfg-masque){position:absolute;inset:0;z-index:2;pointer-events:none;box-shadow:0 0 0 100vmax var(--reflet-cfg-voile,rgba(245,242,236,.86));outline:1px solid rgba(28,27,25,.35)}',
    ':where(.reflet-cfg-securite){position:absolute;z-index:3;pointer-events:none;border:1px dashed rgba(28,27,25,.6)}',
    ':where(.reflet-cfg-vide){position:absolute;inset:0;z-index:4;display:flex;align-items:center;justify-content:center;margin:0;padding:16px;text-align:center}',
    ':where(.reflet-cfg-zoom){display:flex;align-items:center;gap:8px;margin-top:8px}',
    ':where(.reflet-cfg-zoom-curseur){flex:1 1 auto;min-width:0}',
    ':where(.reflet-cfg-zoom-bouton){min-width:44px;min-height:44px}',
    ':where(.reflet-cfg-jauge-barre){position:relative;display:flex;height:8px;margin:10px 0 6px;border-radius:4px;overflow:visible}',
    ':where(.reflet-cfg-jauge-seg){display:block;height:100%}',
    ':where(.reflet-cfg-jauge-seg--faible){background:var(--reflet-cfg-faible);border-radius:4px 0 0 4px}',
    ':where(.reflet-cfg-jauge-seg--correct){background:var(--reflet-cfg-correct)}',
    ':where(.reflet-cfg-jauge-seg--bon){background:var(--reflet-cfg-bon);border-radius:0 4px 4px 0}',
    ':where(.reflet-cfg-jauge-curseur){position:absolute;top:50%;width:16px;height:16px;margin:-8px 0 0 -8px;border:2px solid #fff;border-radius:50%;background:#1C1B19;box-shadow:0 0 0 1px #1C1B19}',
    ':where(.reflet-cfg-case){display:flex;gap:10px;align-items:flex-start;cursor:pointer}',
    ':where(.reflet-cfg-case input){flex:0 0 auto;width:20px;height:20px;margin:2px 0 0}'
  ].join('\n');

  function injecterStyleBase() {
    if (!doc || doc.querySelector('style[data-reflet-cfg-base]')) { return; }
    var s = doc.createElement('style');
    s.setAttribute('data-reflet-cfg-base', VERSION);
    s.textContent = CSS_BASE;
    var tete = doc.head || doc.documentElement;
    tete.insertBefore(s, tete.firstChild);
  }

  /* ------------------------------------------------------------------ */
  /* Construction du DOM                                                 */
  /* ------------------------------------------------------------------ */

  function el(tag, attrs, enfants) {
    var n = doc.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] === null || attrs[k] === undefined || attrs[k] === false) { continue; }
        if (k === 'texte') { n.textContent = attrs[k]; }
        else if (k === 'classe') { n.className = attrs[k]; }
        else { n.setAttribute(k, attrs[k] === true ? '' : attrs[k]); }
      }
    }
    if (enfants) {
      for (var i = 0; i < enfants.length; i++) {
        if (enfants[i]) { n.appendChild(typeof enfants[i] === 'string' ? doc.createTextNode(enfants[i]) : enfants[i]); }
      }
    }
    return n;
  }

  var compteurInstances = 0;

  /* ------------------------------------------------------------------ */
  /* Instance                                                            */
  /* ------------------------------------------------------------------ */

  function creer(root, opts) {
    var T = opts.textes;
    var uid = 'reflet-cfg-' + (++compteurInstances);
    var formats = [];
    for (var i = 0; i < (opts.formats || []).length; i++) {
      var nf = normaliserFormat(opts.formats[i], i);
      if (nf) { formats.push(nf); }
    }
    if (!formats.length) { throw new Error('RefletConfigurateur : aucun format valide dans options.formats'); }

    var etat = {
      format: null,
      orientation: opts.orientationInitiale === 'paysage' ? 'paysage' : 'portrait',
      fichier: null,
      orig: null,      // { w, h } dimensions de l'original (orientation appliquée)
      travail: null,   // { url, w, h, k, proprieteUrl } copie de travail affichée
      z: 1,
      cx: 0,
      cy: 0,
      alerteAcceptee: false,
      droitsOK: false,
      chargement: false,
      erreursVisibles: false,
      jeton: 0
    };
    var ecouteurs = [];
    var pointeurs = {};
    var geste = null;
    var rafId = 0;
    var dernierDetail = null;
    var detruit = false;

    function ecouter(cible, type, fn, o) {
      cible.addEventListener(type, fn, o || false);
      ecouteurs.push([cible, type, fn, o || false]);
    }

    function formatParId(id) {
      for (var j = 0; j < formats.length; j++) { if (formats[j].id === String(id)) { return formats[j]; } }
      return null;
    }

    etat.format = formatParId(opts.formatInitial);
    if (!etat.format || !etat.format.disponible) {
      etat.format = null;
      for (var j = 0; j < formats.length; j++) { if (formats[j].disponible) { etat.format = formats[j]; break; } }
      if (!etat.format) { etat.format = formats[0]; }
    }

    /* ---------- DOM ---------- */
    injecterStyleBase();
    root.classList.add('reflet-cfg');
    root.textContent = '';

    var noel = el('p', { classe: 'reflet-cfg-noel', hidden: true });

    // Étape 1 : format
    var listeFormats = el('div', { classe: 'reflet-cfg-formats', role: 'radiogroup', 'aria-labelledby': uid + '-t1' });
    var cartes = {};
    formats.forEach(function (f) {
      var radio = el('input', {
        type: 'radio', name: uid + '-format', value: f.id, classe: 'reflet-cfg-format-radio',
        disabled: !f.disponible
      });
      var pastille = el('span', { classe: 'reflet-cfg-format-nettete', hidden: true });
      var conseil = el('span', { classe: 'reflet-cfg-format-conseil', texte: T.conseille, hidden: true });
      var carte = el('label', { classe: 'reflet-cfg-format' + (f.disponible ? '' : ' is-indisponible'), 'data-format': f.id }, [
        radio,
        el('span', { classe: 'reflet-cfg-format-nom', texte: f.libelle }),
        f.prix !== null ? el('span', { classe: 'reflet-cfg-format-prix', texte: formaterPrix(f.prix, f.devise) }) : null,
        (f.prix !== null && T.notePrix) ? el('span', { classe: 'reflet-cfg-format-note', texte: T.notePrix }) : null,
        pastille,
        conseil
      ]);
      cartes[f.id] = { carte: carte, radio: radio, pastille: pastille, conseil: conseil };
      listeFormats.appendChild(carte);
    });
    var etapeFormat = el('div', { classe: 'reflet-cfg-etape reflet-cfg-etape--format' }, [
      el('p', { classe: 'reflet-cfg-titre', id: uid + '-t1', texte: T.titreFormat }),
      listeFormats
    ]);

    // Étape 2 : photo
    var champFichier = el('input', { type: 'file', accept: 'image/*', classe: 'reflet-cfg-fichier', id: uid + '-photo' });
    var libelleBoutonPhoto = el('span', { classe: 'reflet-cfg-bouton-texte', texte: T.boutonPhoto });
    var boutonPhoto = el('label', { classe: 'reflet-cfg-bouton reflet-cfg-bouton--photo' }, [champFichier, libelleBoutonPhoto]);
    var nomFichier = el('p', { classe: 'reflet-cfg-fichier-nom', hidden: true });
    var erreurPhoto = el('p', { classe: 'reflet-cfg-erreur', role: 'alert', hidden: true });
    var etapePhoto = el('div', { classe: 'reflet-cfg-etape reflet-cfg-etape--photo' }, [
      el('p', { classe: 'reflet-cfg-titre', texte: T.titrePhoto }),
      boutonPhoto,
      nomFichier,
      erreurPhoto,
      T.aidePhoto ? el('p', { classe: 'reflet-cfg-aide', texte: T.aidePhoto }) : null
    ]);

    // Étape 3 : cadrage
    var radiosOrientation = {};
    var choixOrientation = {};
    var groupeOrientation = el('div', { classe: 'reflet-cfg-orientation', role: 'radiogroup', 'aria-label': T.orientation });
    ['portrait', 'paysage'].forEach(function (o) {
      var r = el('input', { type: 'radio', name: uid + '-orientation', value: o, classe: 'reflet-cfg-orientation-radio' });
      var c = el('label', { classe: 'reflet-cfg-orientation-choix reflet-cfg-orientation-choix--' + o }, [
        r, el('span', { classe: 'reflet-cfg-orientation-nom', texte: T[o] })
      ]);
      radiosOrientation[o] = r;
      choixOrientation[o] = c;
      groupeOrientation.appendChild(c);
    });

    var image = el('img', { classe: 'reflet-cfg-image', alt: '', draggable: 'false', hidden: true });
    var masque = el('div', { classe: 'reflet-cfg-masque', 'aria-hidden': 'true' });
    var securite = el('div', { classe: 'reflet-cfg-securite', 'aria-hidden': 'true', hidden: true });
    var cadre = el('div', { classe: 'reflet-cfg-cadre' }, [image, masque, securite]);
    var vide = el('p', { classe: 'reflet-cfg-vide', texte: T.zoneVide });
    var zone = el('div', {
      classe: 'reflet-cfg-zone is-vide', tabindex: '0', role: 'group',
      'aria-roledescription': 'recadrage', 'aria-label': T.zoneAria
    }, [cadre, vide]);

    var zoomMoins = el('button', { type: 'button', classe: 'reflet-cfg-bouton reflet-cfg-zoom-bouton reflet-cfg-zoom-bouton--moins', 'aria-label': T.zoomMoins, texte: '−' });
    var zoomCurseur = el('input', {
      type: 'range', classe: 'reflet-cfg-zoom-curseur', min: '1', max: String(opts.zoomMax), step: '0.01', value: '1',
      'aria-label': T.zoom
    });
    var zoomPlus = el('button', { type: 'button', classe: 'reflet-cfg-bouton reflet-cfg-zoom-bouton reflet-cfg-zoom-bouton--plus', 'aria-label': T.zoomPlus, texte: '+' });
    var boutonRecentrer = el('button', { type: 'button', classe: 'reflet-cfg-bouton reflet-cfg-recentrer', texte: T.recentrer });
    var controlesZoom = el('div', { classe: 'reflet-cfg-zoom', hidden: true }, [zoomMoins, zoomCurseur, zoomPlus, boutonRecentrer]);
    var consigne = el('p', { classe: 'reflet-cfg-consigne', hidden: true }, [T.consigne + ' ', el('span', { classe: 'reflet-cfg-consigne-securite', texte: T.securite })]);

    var jaugeStatut = el('strong', { classe: 'reflet-cfg-jauge-statut' });
    var jaugeDetail = el('span', { classe: 'reflet-cfg-jauge-detail' });
    var jaugeDpi = el('p', { classe: 'reflet-cfg-jauge-dpi' });
    var segFaible = el('span', { classe: 'reflet-cfg-jauge-seg reflet-cfg-jauge-seg--faible' });
    var segCorrect = el('span', { classe: 'reflet-cfg-jauge-seg reflet-cfg-jauge-seg--correct' });
    var segBon = el('span', { classe: 'reflet-cfg-jauge-seg reflet-cfg-jauge-seg--bon' });
    var jaugeCurseur = el('span', { classe: 'reflet-cfg-jauge-curseur' });
    var jaugeBarre = el('div', { classe: 'reflet-cfg-jauge-barre', 'aria-hidden': 'true' }, [segFaible, segCorrect, segBon, jaugeCurseur]);
    var suggestionTexte = el('span', { classe: 'reflet-cfg-suggestion-texte' });
    var suggestionBouton = el('button', { type: 'button', classe: 'reflet-cfg-bouton reflet-cfg-suggestion-bouton' });
    var suggestion = el('p', { classe: 'reflet-cfg-suggestion', hidden: true }, [suggestionTexte, ' ', suggestionBouton]);
    var dezoomer = el('p', { classe: 'reflet-cfg-dezoomer', texte: T.dezoomer, hidden: true });
    var caseForcer = el('input', { type: 'checkbox', classe: 'reflet-cfg-forcer-case' });
    var forcer = el('label', { classe: 'reflet-cfg-case reflet-cfg-forcer', hidden: true }, [caseForcer, el('span', { texte: T.forcer })]);
    var jauge = el('div', { classe: 'reflet-cfg-jauge', role: 'status', 'aria-live': 'polite', hidden: true }, [
      el('p', { classe: 'reflet-cfg-jauge-texte' }, [jaugeStatut, ' ', jaugeDetail]),
      jaugeBarre,
      jaugeDpi,
      suggestion,
      dezoomer,
      forcer
    ]);

    var etapeCadrage = el('div', { classe: 'reflet-cfg-etape reflet-cfg-etape--cadrage' }, [
      el('p', { classe: 'reflet-cfg-titre', texte: T.titreCadrage }),
      groupeOrientation,
      zone,
      controlesZoom,
      consigne,
      jauge
    ]);

    // Étape 4 : droits, livraison, bouton
    var caseDroits = el('input', { type: 'checkbox', classe: 'reflet-cfg-droits-case', id: uid + '-droits' });
    var lienConf = opts.liens && opts.liens.confidentialite
      ? el('a', { classe: 'reflet-cfg-lien', href: opts.liens.confidentialite, target: '_blank', rel: 'noopener', texte: T.lienConfidentialite })
      : null;
    var droits = el('label', { classe: 'reflet-cfg-case reflet-cfg-droits' }, [
      caseDroits,
      el('span', { classe: 'reflet-cfg-droits-texte' }, [T.droits + (lienConf ? ' ' : ''), lienConf])
    ]);
    var conservation = el('p', { classe: 'reflet-cfg-conservation', texte: gabarit(T.conservation, { delai: opts.conservation }) });
    if (!opts.conservation) { conservation.hidden = true; }

    var livraisonDate = el('strong', { classe: 'reflet-cfg-livraison-date' });
    var livraison = el('p', { classe: 'reflet-cfg-livraison' });
    var apresNoel = el('p', { classe: 'reflet-cfg-apres-noel', texte: T.apresNoel, hidden: true });
    var recap = el('p', { classe: 'reflet-cfg-recap' });
    var erreurs = el('div', { classe: 'reflet-cfg-erreurs', role: 'alert', hidden: true });
    var boutonPanier = opts.bouton ? el('button', { type: 'button', classe: 'reflet-cfg-bouton reflet-cfg-bouton--panier', texte: typeof opts.bouton === 'string' ? opts.bouton : T.bouton }) : null;

    var etapeFin = el('div', { classe: 'reflet-cfg-etape reflet-cfg-etape--fin' }, [
      droits, conservation, livraison, apresNoel, recap, erreurs, boutonPanier
    ]);

    var sansInterface = opts.interface === false;
    var avecRecadrage = opts.recadrage !== false;
    var avecAlerte = opts.alerteNettete !== false;
    var droitsRequis = opts.droitsRequis !== false;
    if (!droitsRequis) { droits.hidden = true; }
    if (!avecRecadrage) {
      zone.classList.add('is-fixe');
      zone.removeAttribute('tabindex');
      zone.setAttribute('aria-label', T.titreCadrage);
    }
    if (sansInterface) {
      // Moteur seul : le champ fichier (caché) suffit. Les autres nœuds restent hors du document.
      champFichier.setAttribute('tabindex', '-1');
      champFichier.setAttribute('aria-hidden', 'true');
      root.appendChild(champFichier);
    } else {
      root.appendChild(noel);
      root.appendChild(etapeFormat);
      root.appendChild(etapePhoto);
      root.appendChild(etapeCadrage);
      root.appendChild(etapeFin);
    }

    /* ---------- calculs liés à l'état ---------- */

    function ratio() { return ratioFormat(etat.format, etat.orientation); }

    function cropOrig() {
      if (!etat.orig) { return null; }
      var m = recadrageMax(etat.orig.w, etat.orig.h, ratio());
      var w = m.w / etat.z;
      var h = m.h / etat.z;
      return { x: etat.cx - w / 2, y: etat.cy - h / 2, w: w, h: h };
    }

    function bornerEtat() {
      if (!etat.orig) { return; }
      etat.z = borner(etat.z, 1, opts.zoomMax);
      var c = cropOrig();
      etat.cx = borner(etat.cx, c.w / 2, etat.orig.w - c.w / 2);
      etat.cy = borner(etat.cy, c.h / 2, etat.orig.h - c.h / 2);
    }

    function aujourdhui() { return estIso(opts.aujourdhui) ? opts.aujourdhui : aujourdhuiIso(opts.fuseau); }

    function niveauActuel() {
      var c = cropOrig();
      if (!c) { return { dpi: null, niveau: null }; }
      var d = dimensionsCm(etat.format, etat.orientation);
      var dpi = calculerDpi(c.w, c.h, d.largeur, d.hauteur);
      return { dpi: dpi, niveau: niveauNettete(dpi, opts.seuils) };
    }

    function erreursValidation() {
      var liste = [];
      if (!etat.fichier || !etat.orig) { liste.push({ code: 'photo', message: T.manquePhoto }); }
      if (!etat.format.disponible) { liste.push({ code: 'format', message: T.manqueFormat }); }
      var n = niveauActuel();
      if (avecAlerte && n.niveau === 'faible' && !etat.alerteAcceptee) { liste.push({ code: 'nettete', message: T.manqueNettete }); }
      if (droitsRequis && !etat.droitsOK) { liste.push({ code: 'droits', message: T.manqueDroits }); }
      return liste;
    }

    function construireEtat() {
      var c = cropOrig();
      var n = niveauActuel();
      var d = dimensionsCm(etat.format, etat.orientation);
      var auj = aujourdhui();
      var est = estimationLivraison(auj, opts.delais, opts.fermetures);
      var noelOpts = opts.noel || {};
      var coh = coherenceNoel(noelOpts.date, opts.delais, opts.fermetures, noelOpts.dernierDepot);
      var conseil = etat.orig ? formatConseille(formats, etat.orig.w, etat.orig.h, etat.orientation, opts.seuils, 'bon') ||
        formatConseille(formats, etat.orig.w, etat.orig.h, etat.orientation, opts.seuils, 'correct') : null;
      var plusPetit = (etat.orig && n.niveau === 'faible')
        ? formatPlusPetit(formats, etat.format, etat.orig.w, etat.orig.h, etat.orientation, opts.seuils) : null;
      var crop = c ? { x: Math.round(c.x), y: Math.round(c.y), w: Math.round(c.w), h: Math.round(c.h) } : null;
      var liste = erreursValidation();
      var f = etat.format;
      return {
        imageUrl: etat.travail ? etat.travail.url : null,
        imageNaturalW: etat.orig ? etat.orig.w : null,
        imageNaturalH: etat.orig ? etat.orig.h : null,
        imageUrlW: etat.travail ? etat.travail.w : null,
        imageUrlH: etat.travail ? etat.travail.h : null,
        crop: crop,
        cropNorm: c ? { x: c.x / etat.orig.w, y: c.y / etat.orig.h, w: c.w / etat.orig.w, h: c.h / etat.orig.h } : null,
        format: { id: f.id, w: f.w, h: f.h, prix: f.prix, devise: f.devise, libelle: f.libelle, variantId: f.variantId },
        dimensionsCm: d,
        ratio: d.largeur / d.hauteur,
        orientation: etat.orientation,
        zoom: etat.z,
        dpi: n.dpi === null ? null : Math.round(n.dpi),
        niveau: n.niveau,
        formatConseille: conseil ? conseil.id : null,
        formatSuggere: plusPetit ? plusPetit.id : null,
        alerteAcceptee: etat.alerteAcceptee,
        droitsOK: etat.droitsOK,
        fichierNom: etat.fichier ? etat.fichier.name : null,
        livraison: { depot: est.depot, date: est.livraison, texte: formaterDateFr(est.livraison) },
        noel: {
          date: noelOpts.date || null,
          visible: noelVisible(auj, noelOpts.date),
          texte: gabarit(noelOpts.texte, { date: formaterDateFr(noelOpts.date) }),
          depotCalcule: coh.depot,
          coherent: coh.coherent
        },
        aujourdhui: auj,
        pret: liste.length === 0,
        erreurs: liste.map(function (e) { return e.code; })
      };
    }

    /* ---------- rendu ---------- */

    function rendreImage() {
      if (!etat.travail || !etat.orig) { return; }
      var fw = cadre.clientWidth;
      var fh = cadre.clientHeight;
      if (!fw || !fh) { return; }
      var c = cropOrig();
      var echelle = fw / c.w;                    // px CSS par pixel de l'original
      var s = echelle / etat.travail.k;          // px CSS par pixel de la copie de travail
      var tx = fw / 2 - etat.cx * echelle;
      var ty = fh / 2 - etat.cy * echelle;
      image.style.transform = 'translate(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px) scale(' + s.toFixed(5) + ')';
    }

    function rendreRatio() {
      var r = ratio();
      zone.style.setProperty('--reflet-cfg-ratio', String(r));
      cadre.style.setProperty('--reflet-cfg-ratio', String(r));
      var d = dimensionsCm(etat.format, etat.orientation);
      var mm = Number(opts.margeSecuriteMm) || 0;
      if (mm > 0) {
        var px = (mm / 10) / d.largeur * 100;
        var py = (mm / 10) / d.hauteur * 100;
        securite.style.left = px + '%';
        securite.style.right = px + '%';
        securite.style.top = py + '%';
        securite.style.bottom = py + '%';
      }
    }

    function rendreControles(e) {
      formats.forEach(function (f) {
        var c = cartes[f.id];
        var actif = f.id === etat.format.id;
        c.radio.checked = actif;
        c.carte.classList.toggle('is-actif', actif);
        if (etat.orig && avecAlerte) {
          var dpi = dpiPourFormat(etat.orig.w, etat.orig.h, f, etat.orientation, 1);
          var niv = niveauNettete(dpi, opts.seuils);
          c.pastille.hidden = false;
          c.pastille.className = 'reflet-cfg-format-nettete reflet-cfg-niveau--' + niv;
          c.pastille.textContent = T['niveau_' + niv];
          c.carte.setAttribute('data-niveau', niv);
          c.conseil.hidden = e.formatConseille !== f.id;
          c.carte.classList.toggle('is-conseille', e.formatConseille === f.id);
        } else {
          c.pastille.hidden = true;
          c.conseil.hidden = true;
          c.carte.removeAttribute('data-niveau');
          c.carte.classList.remove('is-conseille');
        }
      });
      ['portrait', 'paysage'].forEach(function (o) {
        radiosOrientation[o].checked = etat.orientation === o;
        choixOrientation[o].classList.toggle('is-actif', etat.orientation === o);
      });
      root.setAttribute('data-reflet-orientation', etat.orientation);
      root.setAttribute('data-reflet-format', etat.format.id);

      var aPhoto = !!(etat.travail && etat.orig);
      zone.classList.toggle('is-vide', !aPhoto);
      vide.hidden = aPhoto || etat.chargement;
      image.hidden = !aPhoto;
      securite.hidden = !aPhoto || !avecRecadrage || !(Number(opts.margeSecuriteMm) > 0);
      controlesZoom.hidden = !aPhoto || !avecRecadrage;
      consigne.hidden = !aPhoto || !avecRecadrage;
      libelleBoutonPhoto.textContent = etat.chargement ? T.chargement : (aPhoto ? T.boutonPhotoChanger : T.boutonPhoto);
      zoomCurseur.value = String(etat.z);
      zoomMoins.disabled = etat.z <= 1.0001;
      zoomPlus.disabled = etat.z >= opts.zoomMax - 0.0001;
      if (aPhoto && etat.fichier) {
        nomFichier.hidden = false;
        nomFichier.textContent = etat.fichier.name + ' · ' + etat.orig.w + ' × ' + etat.orig.h + ' px';
      } else {
        nomFichier.hidden = true;
      }

      // Jauge (absente si alerteNettete: false : la netteté reste dans l'état et le FormData)
      jauge.hidden = !aPhoto || !avecAlerte;
      if (aPhoto && avecAlerte) {
        var niveau = e.niveau;
        jauge.className = 'reflet-cfg-jauge reflet-cfg-niveau--' + niveau;
        root.setAttribute('data-reflet-niveau', niveau);
        jaugeStatut.textContent = T['niveau_' + niveau];
        jaugeDetail.textContent = gabarit(T['detail_' + niveau], { format: etat.format.libelle });
        jaugeDpi.textContent = gabarit(T.dpi, { dpi: e.dpi });
        var echelleMax = opts.seuils.bon * 4 / 3;
        var pFaible = borner(opts.seuils.min / echelleMax * 100, 0, 100);
        var pCorrect = borner((opts.seuils.bon - opts.seuils.min) / echelleMax * 100, 0, 100 - pFaible);
        segFaible.style.width = pFaible + '%';
        segCorrect.style.width = pCorrect + '%';
        segBon.style.width = (100 - pFaible - pCorrect) + '%';
        jaugeCurseur.style.left = borner(e.dpi / echelleMax * 100, 0, 100) + '%';
        var faible = niveau === 'faible';
        var sugg = faible && e.formatSuggere ? formatParId(e.formatSuggere) : null;
        suggestion.hidden = !sugg;
        if (sugg) {
          suggestionTexte.textContent = gabarit(T.suggestion, { format: sugg.libelle });
          suggestionBouton.textContent = gabarit(T.suggestionBouton, { format: sugg.libelle });
          suggestionBouton.setAttribute('data-format', sugg.id);
        }
        var dezoomUtile = faible && etat.z > 1.0001 &&
          dpiPourFormat(etat.orig.w, etat.orig.h, etat.format, etat.orientation, 1) >= opts.seuils.min;
        dezoomer.hidden = !dezoomUtile;
        forcer.hidden = !faible;
        caseForcer.checked = etat.alerteAcceptee;
      } else {
        root.removeAttribute('data-reflet-niveau');
      }

      caseDroits.checked = etat.droitsOK;

      // Livraison, Noël, récapitulatif
      livraison.textContent = '';
      var morceaux = String(T.livraison).split('[date]');
      livraison.appendChild(doc.createTextNode(morceaux[0] || ''));
      livraisonDate.textContent = e.livraison.texte;
      livraison.appendChild(livraisonDate);
      if (morceaux[1]) { livraison.appendChild(doc.createTextNode(morceaux[1])); }
      if (T.livraisonNote) { livraison.appendChild(el('span', { classe: 'reflet-cfg-livraison-note', texte: ' (' + T.livraisonNote + ')' })); }
      var annee = e.aujourdhui.slice(0, 4);
      var veille = annee + '-12-24';
      apresNoel.hidden = !(e.aujourdhui <= veille && e.livraison.date > veille);
      noel.hidden = !e.noel.visible;
      noel.textContent = e.noel.texte;

      var bouts = [etat.format.libelle, T[etat.orientation]];
      if (etat.format.prix !== null) { bouts.push(formaterPrix(etat.format.prix, etat.format.devise)); }
      recap.textContent = bouts.join(' · ');

      if (boutonPanier) {
        boutonPanier.classList.toggle('is-bloque', !e.pret);
        boutonPanier.setAttribute('aria-disabled', e.pret ? 'false' : 'true');
      }
      if (etat.erreursVisibles) { afficherErreurs(erreursValidation()); }
      root.classList.toggle('is-pret', e.pret);
      root.classList.toggle('a-photo', aPhoto);
    }

    function afficherErreurs(liste) {
      erreurs.textContent = '';
      erreurs.hidden = !liste.length;
      liste.forEach(function (x) {
        erreurs.appendChild(el('p', { classe: 'reflet-cfg-erreurs-ligne reflet-cfg-erreurs-ligne--' + x.code, texte: x.message }));
      });
    }

    function emettre() {
      var e = construireEtat();
      dernierDetail = e;
      rendreControles(e);
      var evt;
      try {
        evt = new CustomEvent('reflet:change', { detail: e, bubbles: true });
      } catch (err) {
        evt = doc.createEvent('CustomEvent');
        evt.initCustomEvent('reflet:change', true, false, e);
      }
      root.dispatchEvent(evt);
      return e;
    }

    /* Une mise à jour (rendu + événement) par image affichée au maximum.
       requestAnimationFrame ne tourne pas dans un onglet en arrière-plan : une
       minuterie de secours fait partir 'reflet:change' quand même. */
    function planifier() {
      if (detruit || rafId) { return; }
      var fait = false;
      function lancer() {
        if (fait) { return; }
        fait = true;
        rafId = 0;
        if (detruit) { return; }
        rendreImage();
        emettre();
      }
      rafId = 1;
      if (win.requestAnimationFrame) { win.requestAnimationFrame(lancer); }
      setTimeout(lancer, 60);
    }

    /* ---------- actions ---------- */

    function setFormat(id) {
      var f = formatParId(id);
      if (!f || !f.disponible) { return false; }
      if (f.id !== etat.format.id) { etat.alerteAcceptee = false; }
      etat.format = f;
      rendreRatio();
      bornerEtat();
      planifier();
      return true;
    }

    function setOrientation(o) {
      if (o !== 'portrait' && o !== 'paysage') { return false; }
      if (o !== etat.orientation) { etat.alerteAcceptee = false; }
      etat.orientation = o;
      rendreRatio();
      bornerEtat();
      planifier();
      return true;
    }

    function recentrer() {
      if (!etat.orig) { return; }
      etat.z = 1;
      etat.cx = etat.orig.w / 2;
      etat.cy = etat.orig.h / 2;
      bornerEtat();
      planifier();
    }

    function zoomerEn(clientX, clientY, nouveauZ) {
      if (!etat.orig) { return; }
      var r = cadre.getBoundingClientRect();
      if (!r.width) { return; }
      var c1 = cropOrig();
      var e1 = r.width / c1.w;
      var px = clientX - (r.left + r.width / 2);
      var py = clientY - (r.top + r.height / 2);
      var ix = etat.cx + px / e1;
      var iy = etat.cy + py / e1;
      etat.z = borner(nouveauZ, 1, opts.zoomMax);
      var c2 = cropOrig();
      var e2 = r.width / c2.w;
      etat.cx = ix - px / e2;
      etat.cy = iy - py / e2;
      bornerEtat();
      planifier();
    }

    function zoomerCentre(nouveauZ) {
      var r = cadre.getBoundingClientRect();
      zoomerEn(r.left + r.width / 2, r.top + r.height / 2, nouveauZ);
    }

    function deplacer(dxCss, dyCss) {
      if (!etat.orig) { return; }
      var fw = cadre.clientWidth;
      if (!fw) { return; }
      var echelle = fw / cropOrig().w;
      etat.cx -= dxCss / echelle;
      etat.cy -= dyCss / echelle;
      bornerEtat();
      planifier();
    }

    function erreurFichier(message, code) {
      erreurPhoto.textContent = message;
      erreurPhoto.hidden = false;
      try { champFichier.value = ''; } catch (e) { /* rien */ }
      var evt;
      var detail = { code: code || 'photo', message: message };
      try { evt = new CustomEvent('reflet:erreur', { detail: detail, bubbles: true }); }
      catch (err) { evt = doc.createEvent('CustomEvent'); evt.initCustomEvent('reflet:erreur', true, false, detail); }
      root.dispatchEvent(evt);
    }

    function libererTravail() {
      if (etat.travail && etat.travail.url && etat.travail.revocable && win.URL && URL.revokeObjectURL) {
        URL.revokeObjectURL(etat.travail.url);
      }
      etat.travail = null;
    }

    function chargerFichier(fichier) {
      if (!fichier) { return; }
      erreurPhoto.hidden = true;
      var max = Number(opts.tailleMaxMo);
      if (max > 0 && fichier.size > max * 1024 * 1024) {
        erreurFichier(gabarit(T.erreurTaille, {
          taille: (fichier.size / 1048576).toFixed(1).replace('.', ','),
          max: String(max).replace('.', ',')
        }), 'taille');
        return;
      }
      var heic = estHeic(fichier);
      if (fichier.type && !/^image\//i.test(fichier.type) && !heic) { erreurFichier(T.erreurType, 'type'); return; }

      var jeton = ++etat.jeton;
      etat.chargement = true;
      planifier();
      var urlSource = URL.createObjectURL(fichier);
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        if (jeton !== etat.jeton || detruit) { URL.revokeObjectURL(urlSource); return; }
        var ow = img.naturalWidth;
        var oh = img.naturalHeight;
        if (!ow || !oh) { echec(); return; }
        var k = Math.min(1, (opts.travail.cote || 2048) / Math.max(ow, oh));
        function terminer(url, w, h, revocable) {
          if (jeton !== etat.jeton || detruit) { if (revocable) { URL.revokeObjectURL(url); } return; }
          libererTravail();
          etat.fichier = fichier;
          etat.orig = { w: ow, h: oh };
          etat.travail = { url: url, w: w, h: h, k: w / ow, revocable: revocable };
          etat.chargement = false;
          etat.alerteAcceptee = false;
          if (opts.orientationAuto && ow !== oh) { etat.orientation = ow > oh ? 'paysage' : 'portrait'; }
          image.style.width = w + 'px';
          image.style.height = h + 'px';
          image.src = url;
          rendreRatio();
          recentrer();
        }
        if (k >= 1) { terminer(urlSource, ow, oh, true); return; }
        var cw = Math.max(1, Math.round(ow * k));
        var ch = Math.max(1, Math.round(oh * k));
        try {
          var canvas = doc.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, cw, ch);
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, cw, ch);
          canvas.toBlob(function (blob) {
            URL.revokeObjectURL(urlSource);
            if (!blob) { echec(); return; }
            terminer(URL.createObjectURL(blob), cw, ch, true);
          }, 'image/jpeg', 0.9);
        } catch (err) {
          URL.revokeObjectURL(urlSource);
          echec();
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(urlSource);
        if (jeton !== etat.jeton) { return; }
        echec();
      };
      function echec() {
        etat.chargement = false;
        erreurFichier(heic ? T.erreurHeic : T.erreurLecture, heic ? 'heic' : 'lecture');
        planifier();
      }
      img.src = urlSource;
    }

    function reset() {
      etat.jeton++;
      libererTravail();
      etat.fichier = null;
      etat.orig = null;
      etat.z = 1;
      etat.alerteAcceptee = false;
      etat.droitsOK = false;
      etat.chargement = false;
      etat.erreursVisibles = false;
      image.removeAttribute('src');
      erreurPhoto.hidden = true;
      erreurs.hidden = true;
      try { champFichier.value = ''; } catch (e) { /* rien */ }
      planifier();
    }

    /* Aperçu recadré, synchrone (toDataURL) pour que getFormData() reste synchrone. */
    function apercuBlob() {
      if (!etat.orig || !etat.travail || !image.complete || !image.naturalWidth) { return null; }
      var c = cropOrig();
      var k = etat.travail.k;
      var sw = c.w * k;
      var sh = c.h * k;
      var cote = opts.apercu.cote || 1200;
      var r = c.w / c.h;
      var W = r >= 1 ? cote : Math.round(cote * r);
      var H = r >= 1 ? Math.round(cote / r) : cote;
      if (W > sw) { W = Math.max(1, Math.round(sw)); H = Math.max(1, Math.round(sh)); }
      try {
        var canvas = doc.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, c.x * k, c.y * k, sw, sh, 0, 0, W, H);
        return dataUrlVersBlob(canvas.toDataURL('image/jpeg', opts.apercu.qualite || 0.85));
      } catch (err) {
        return null;
      }
    }

    function getFormData() {
      var fd = new FormData();
      var ch = opts.champs || {};
      var e = construireEtat();
      function ajouter(cle, valeur, nom) {
        if (!cle || valeur === null || valeur === undefined) { return; }
        if (nom) { fd.append(cle, valeur, nom); } else { fd.append(cle, valeur); }
      }
      ajouter(ch.variante, etat.format.variantId != null ? String(etat.format.variantId) : null);
      ajouter(ch.quantite, '1');
      ajouter(ch.format, etat.format.libelle);
      ajouter(ch.orientation, T[etat.orientation]);
      if (etat.fichier) { ajouter(ch.photo, etat.fichier, etat.fichier.name || 'photo.jpg'); }
      var blob = apercuBlob();
      if (blob) { ajouter(ch.apercu, blob, 'apercu-recadre.jpg'); }
      if (e.crop) { ajouter(ch.recadrage, texteRecadrage(e.crop, e.imageNaturalW, e.imageNaturalH)); }
      if (e.niveau) { ajouter(ch.nettete, e.niveau + ' (' + e.dpi + ' dpi)'); }
      if (avecAlerte && e.niveau === 'faible' && etat.alerteAcceptee) { ajouter(ch.alerte, 'Oui'); }
      if (!droitsRequis) { ajouter(ch.droits, 'Confirmés à l’ajout au panier'); }
      else if (etat.droitsOK) { ajouter(ch.droits, 'Confirmés'); }
      ajouter(ch.livraison, e.livraison.date);
      return fd;
    }

    function validate() {
      var liste = erreursValidation();
      return { ok: liste.length === 0, erreurs: liste };
    }

    /* ---------- écouteurs ---------- */

    formats.forEach(function (f) {
      ecouter(cartes[f.id].radio, 'change', function () { if (cartes[f.id].radio.checked) { setFormat(f.id); } });
    });
    ['portrait', 'paysage'].forEach(function (o) {
      ecouter(radiosOrientation[o], 'change', function () { if (radiosOrientation[o].checked) { setOrientation(o); } });
    });
    ecouter(champFichier, 'change', function () {
      if (champFichier.files && champFichier.files[0]) { chargerFichier(champFichier.files[0]); }
    });

    // Glisser-déposer un fichier sur la zone (ordinateur)
    ecouter(zone, 'dragover', function (ev) { ev.preventDefault(); zone.classList.add('is-survol'); });
    ecouter(zone, 'dragleave', function () { zone.classList.remove('is-survol'); });
    ecouter(zone, 'drop', function (ev) {
      ev.preventDefault();
      zone.classList.remove('is-survol');
      var fl = ev.dataTransfer && ev.dataTransfer.files;
      if (fl && fl[0]) { chargerFichier(fl[0]); }
    });

    // Doigt et souris : 1 pointeur = déplacer, 2 pointeurs = pincer pour zoomer
    function nbPointeurs() { return Object.keys(pointeurs).length; }
    function mesureGeste() {
      var ids = Object.keys(pointeurs);
      var a = pointeurs[ids[0]];
      var b = pointeurs[ids[1]];
      return { dist: Math.hypot(b.x - a.x, b.y - a.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    }
    ecouter(zone, 'pointerdown', function (ev) {
      if (!etat.orig || !avecRecadrage) { return; }
      if (ev.pointerType === 'mouse' && ev.button !== 0) { return; }
      ev.preventDefault();
      try { zone.setPointerCapture(ev.pointerId); } catch (err) { /* rien */ }
      pointeurs[ev.pointerId] = { x: ev.clientX, y: ev.clientY };
      zone.classList.add('is-glisse');
      geste = nbPointeurs() === 2 ? mesureGeste() : null;
    });
    ecouter(zone, 'pointermove', function (ev) {
      var p = pointeurs[ev.pointerId];
      if (!p) { return; }
      ev.preventDefault();
      var n = nbPointeurs();
      if (n === 1) {
        var dx = ev.clientX - p.x;
        var dy = ev.clientY - p.y;
        p.x = ev.clientX;
        p.y = ev.clientY;
        deplacer(dx, dy);
      } else if (n === 2) {
        p.x = ev.clientX;
        p.y = ev.clientY;
        var m = mesureGeste();
        if (geste && geste.dist > 0) {
          zoomerEn(m.mx, m.my, etat.z * (m.dist / geste.dist));
          deplacer(m.mx - geste.mx, m.my - geste.my);
        }
        geste = m;
      }
    });
    function finPointeur(ev) {
      delete pointeurs[ev.pointerId];
      geste = nbPointeurs() === 2 ? mesureGeste() : null;
      if (!nbPointeurs()) { zone.classList.remove('is-glisse'); }
    }
    ecouter(zone, 'pointerup', finPointeur);
    ecouter(zone, 'pointercancel', finPointeur);
    ecouter(zone, 'lostpointercapture', finPointeur);
    ecouter(zone, 'wheel', function (ev) {
      if (!etat.orig || !avecRecadrage) { return; }
      ev.preventDefault();
      var delta = ev.deltaMode === 1 ? ev.deltaY * 16 : ev.deltaY;
      zoomerEn(ev.clientX, ev.clientY, etat.z * Math.exp(-delta * 0.0015));
    }, { passive: false });
    ecouter(zone, 'keydown', function (ev) {
      if (!etat.orig || !avecRecadrage) { return; }
      var pas = ev.shiftKey ? 50 : 10;
      var k = ev.key;
      if (k === 'ArrowLeft') { deplacer(pas, 0); }
      else if (k === 'ArrowRight') { deplacer(-pas, 0); }
      else if (k === 'ArrowUp') { deplacer(0, pas); }
      else if (k === 'ArrowDown') { deplacer(0, -pas); }
      else if (k === '+' || k === '=') { zoomerCentre(etat.z * 1.1); }
      else if (k === '-' || k === '_') { zoomerCentre(etat.z / 1.1); }
      else if (k === '0') { recentrer(); }
      else { return; }
      ev.preventDefault();
    });

    ecouter(zoomCurseur, 'input', function () { zoomerCentre(Number(zoomCurseur.value)); });
    ecouter(zoomMoins, 'click', function () { zoomerCentre(etat.z / 1.15); });
    ecouter(zoomPlus, 'click', function () { zoomerCentre(etat.z * 1.15); });
    ecouter(boutonRecentrer, 'click', recentrer);
    ecouter(suggestionBouton, 'click', function () {
      var id = suggestionBouton.getAttribute('data-format');
      if (id) { setFormat(id); }
    });
    ecouter(caseForcer, 'change', function () { etat.alerteAcceptee = caseForcer.checked; planifier(); });
    ecouter(caseDroits, 'change', function () { etat.droitsOK = caseDroits.checked; planifier(); });

    if (boutonPanier) {
      ecouter(boutonPanier, 'click', function () {
        var v = validate();
        etat.erreursVisibles = true;
        afficherErreurs(v.erreurs);
        if (!v.ok) {
          var premier = v.erreurs[0].code;
          var cible = premier === 'photo' ? boutonPhoto : (premier === 'nettete' ? jauge : (premier === 'droits' ? droits : etapeFormat));
          if (cible.scrollIntoView) { cible.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
          if (premier === 'photo') { champFichier.focus(); }
          else if (premier === 'droits') { caseDroits.focus(); }
          else if (premier === 'nettete') { caseForcer.focus(); }
          return;
        }
        var detail = { formData: getFormData(), etat: construireEtat() };
        var evt;
        try { evt = new CustomEvent('reflet:submit', { detail: detail, bubbles: true }); }
        catch (err) { evt = doc.createEvent('CustomEvent'); evt.initCustomEvent('reflet:submit', true, false, detail); }
        root.dispatchEvent(evt);
        if (typeof opts.onSubmit === 'function') { opts.onSubmit(detail); }
      });
    }

    var observateur = null;
    if (win.ResizeObserver) {
      observateur = new win.ResizeObserver(function () { planifier(); });
      observateur.observe(zone);
    } else {
      ecouter(win, 'resize', planifier);
    }

    function destroy() {
      detruit = true;
      if (observateur) { observateur.disconnect(); }
      ecouteurs.forEach(function (x) { x[0].removeEventListener(x[1], x[2], x[3]); });
      ecouteurs = [];
      libererTravail();
      root.textContent = '';
      root.classList.remove('reflet-cfg', 'is-pret', 'a-photo');
      ['data-reflet-orientation', 'data-reflet-format', 'data-reflet-niveau'].forEach(function (a) { root.removeAttribute(a); });
      if (root.__refletConfigurateur === instance) { delete root.__refletConfigurateur; }
    }

    /* Avertissement développeur : date limite de Noël intenable avec les délais réglés. */
    var coh = coherenceNoel(opts.noel && opts.noel.date, opts.delais, opts.fermetures, opts.noel && opts.noel.dernierDepot);
    if (coh.coherent === false && win.console && console.warn) {
      console.warn('[RefletConfigurateur] Date limite de Noël ' + opts.noel.date + ' : dépôt calculé le ' + coh.depot +
        ', après le dernier dépôt du ' + opts.noel.dernierDepot + ' avec les délais réglés.');
    }

    rendreRatio();
    var instance = {
      version: VERSION,
      element: root,
      getFormData: getFormData,
      getState: function () { return construireEtat(); },
      validate: validate,
      setFormat: setFormat,
      setOrientation: setOrientation,
      loadFile: chargerFichier,
      ouvrirFichier: function () { try { champFichier.click(); } catch (e) { /* rien */ } },
      recentrer: recentrer,
      reset: reset,
      destroy: destroy
    };
    root.__refletConfigurateur = instance;
    emettre();
    return instance;
  }

  function mount(rootEl, options) {
    if (!doc) { throw new Error('RefletConfigurateur.mount : navigateur requis'); }
    if (!rootEl || rootEl.nodeType !== 1) { throw new Error('RefletConfigurateur.mount : élément racine manquant'); }
    if (rootEl.__refletConfigurateur) { rootEl.__refletConfigurateur.destroy(); }
    return creer(rootEl, fusionner(DEFAUTS, options || {}));
  }

  return {
    version: VERSION,
    mount: mount,
    defauts: DEFAUTS,
    utils: {
      fusionner: fusionner,
      gabarit: gabarit,
      normaliserFormat: normaliserFormat,
      dimensionsCm: dimensionsCm,
      ratioFormat: ratioFormat,
      recadrageMax: recadrageMax,
      calculerDpi: calculerDpi,
      niveauNettete: niveauNettete,
      dpiPourFormat: dpiPourFormat,
      formatConseille: formatConseille,
      formatPlusPetit: formatPlusPetit,
      ajouterJoursOuvres: ajouterJoursOuvres,
      estimationLivraison: estimationLivraison,
      coherenceNoel: coherenceNoel,
      noelVisible: noelVisible,
      formaterDateFr: formaterDateFr,
      formaterPrix: formaterPrix,
      aujourdhuiIso: aujourdhuiIso,
      estHeic: estHeic,
      texteRecadrage: texteRecadrage
    }
  };
});
